import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import logger from '../../utils/logger';
import { isServiceOrLabour } from '../../utils/validation';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { useAuth } from '../Auth/Auth';
import { Navigate } from 'react-router-dom';
import MoonLoader from 'react-spinners/MoonLoader';
import BreadCrumb from '../BreadCrumb';
import { CurrencyDollarIcon, ArrowTrendingUpIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const pages = [
  { name: 'Analytics & Reports', to: '/analytics', current: false },
  { name: 'Financial Analysis', to: '/analytics/financial', current: true }
];

const ProfitAnalysis = () => {
  const { role, loading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Financial data
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalProfit, setTotalProfit] = useState(0);
  const [profitMargin, setProfitMargin] = useState(0);
  const [outstandingInvoices, setOutstandingInvoices] = useState(0);
  const [paidInvoices, setPaidInvoices] = useState(0);

  // Chart data
  const [monthlyFinancials, setMonthlyFinancials] = useState([]);
  const [serviceTypeProfits, setServiceTypeProfits] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState([]);
  const [topProfitableServices, setTopProfitableServices] = useState([]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  useEffect(() => {
    if (role === 'Service Advisor' || role === 'Admin') {
      fetchFinancialData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, selectedYear]);

  const fetchFinancialData = async () => {
    setIsLoading(true);
    try {
      // Fetch invoices data
      const invoicesSnapshot = await getDocs(collection(db, 'newInvoices'));

      let revenueTotal = 0;
      let profitTotal = 0;
      let outstandingCount = 0;
      let paidCount = 0;

      const monthlyData = Array(12).fill(null).map((_, idx) => ({
        month: idx,
        revenue: 0,
        cost: 0,
        profit: 0,
        invoiceCount: 0
      }));

      const serviceProfitMap = {};
      const paymentStatusMap = {
        'Paid': 0,
        'Partially Paid': 0,
        'Unpaid': 0,
        'Overdue': 0
      };

      // Process invoices
      invoicesSnapshot.forEach(doc => {
        const invoice = doc.data();
        const invoiceDate = invoice.invoiceDate?.toDate?.() || invoice.createdAt?.toDate?.();

        if (invoiceDate && invoiceDate.getFullYear() === selectedYear) {
          const month = invoiceDate.getMonth();
          const revenue = invoice.receivable || 0;
          const cost = invoice.totalCost || 0;
          const profit = revenue - cost;

          // Monthly aggregation
          monthlyData[month].revenue += revenue;
          monthlyData[month].cost += cost;
          monthlyData[month].profit += profit;
          monthlyData[month].invoiceCount += 1;

          // Totals
          revenueTotal += revenue;
          profitTotal += profit;

          // Payment status
          const status = invoice.paymentStatus || 'Unpaid';
          if (paymentStatusMap.hasOwnProperty(status)) {
            paymentStatusMap[status] += revenue;
          } else {
            paymentStatusMap['Unpaid'] += revenue;
          }

          if (status === 'Paid') {
            paidCount += 1;
          } else {
            outstandingCount += 1;
          }

          // Service type profits
          if (invoice.cartItems && Array.isArray(invoice.cartItems)) {
            invoice.cartItems.forEach(item => {
              const itemRevenue = (item.price || 0) * (item.quantity || 0);
              const itemCost = (item.costPrice || 0) * (item.quantity || 0);
              const itemProfit = itemRevenue - itemCost;
              const itemType = isServiceOrLabour(item) ? 'Services' : 'Parts';

              if (!serviceProfitMap[itemType]) {
                serviceProfitMap[itemType] = {
                  revenue: 0,
                  cost: 0,
                  profit: 0,
                  count: 0
                };
              }

              serviceProfitMap[itemType].revenue += itemRevenue;
              serviceProfitMap[itemType].cost += itemCost;
              serviceProfitMap[itemType].profit += itemProfit;
              serviceProfitMap[itemType].count += (item.quantity || 0);

              // Track individual services for top profitable
              if (isServiceOrLabour(item)) {
                const serviceName = item.description || 'Unknown Service';
                if (!serviceProfitMap[serviceName]) {
                  serviceProfitMap[serviceName] = {
                    revenue: 0,
                    cost: 0,
                    profit: 0,
                    count: 0,
                    type: 'service'
                  };
                }
                serviceProfitMap[serviceName].revenue += itemRevenue;
                serviceProfitMap[serviceName].cost += itemCost;
                serviceProfitMap[serviceName].profit += itemProfit;
                serviceProfitMap[serviceName].count += (item.quantity || 0);
              }
            });
          }
        }
      });

      // Calculate profit margin
      const margin = revenueTotal > 0 ? ((profitTotal / revenueTotal) * 100) : 0;

      // Set state
      setTotalRevenue(revenueTotal);
      setTotalProfit(profitTotal);
      setProfitMargin(margin);
      setOutstandingInvoices(outstandingCount);
      setPaidInvoices(paidCount);
      setMonthlyFinancials(monthlyData);

      // Service type profits (Services vs Parts only)
      const serviceTypes = ['Services', 'Parts']
        .filter(type => serviceProfitMap[type])
        .map(type => ({
          type,
          ...serviceProfitMap[type],
          margin: serviceProfitMap[type].revenue > 0
            ? ((serviceProfitMap[type].profit / serviceProfitMap[type].revenue) * 100)
            : 0
        }));
      setServiceTypeProfits(serviceTypes);

      // Top profitable individual services
      const topServices = Object.entries(serviceProfitMap)
        .filter(([name, data]) => data.type === 'service')
        .map(([name, data]) => ({
          name,
          ...data,
          margin: data.revenue > 0 ? ((data.profit / data.revenue) * 100) : 0
        }))
        .sort((a, b) => b.profit - a.profit)
        .slice(0, 10);
      setTopProfitableServices(topServices);

      // Payment status breakdown
      const paymentStatusData = Object.entries(paymentStatusMap).map(([status, amount]) => ({
        status,
        amount
      }));
      setPaymentStatus(paymentStatusData);

    } catch (error) {
      logger.error('Error fetching financial data:', error);
      toast.error('Failed to load financial data');
    } finally {
      setIsLoading(false);
    }
  };

  // Chart configurations
  const monthlyRevenueChartData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Revenue',
        data: monthlyFinancials.map(m => m.revenue),
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderColor: 'rgb(16, 185, 129)',
        borderWidth: 2
      },
      {
        label: 'Profit',
        data: monthlyFinancials.map(m => m.profit),
        backgroundColor: 'rgba(99, 102, 241, 0.8)',
        borderColor: 'rgb(99, 102, 241)',
        borderWidth: 2
      }
    ]
  };

  const monthlyRevenueChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { padding: 15, usePointStyle: true }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`;
          }
        }
      }
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: {
          callback: function(value) {
            return formatCurrency(value);
          }
        }
      }
    }
  };

  const profitMarginLineData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [{
      label: 'Profit Margin %',
      data: monthlyFinancials.map(m =>
        m.revenue > 0 ? ((m.profit / m.revenue) * 100) : 0
      ),
      borderColor: 'rgb(249, 115, 22)',
      backgroundColor: 'rgba(249, 115, 22, 0.1)',
      tension: 0.4,
      fill: true,
      pointRadius: 4,
      pointHoverRadius: 6
    }]
  };

  const profitMarginLineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `Profit Margin: ${context.parsed.y.toFixed(1)}%`;
          }
        }
      }
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: {
          callback: function(value) {
            return value + '%';
          }
        }
      }
    }
  };

  const serviceTypeProfitData = {
    labels: serviceTypeProfits.map(s => s.type),
    datasets: [{
      label: 'Profit by Type',
      data: serviceTypeProfits.map(s => s.profit),
      backgroundColor: [
        'rgba(99, 102, 241, 0.8)',
        'rgba(16, 185, 129, 0.8)'
      ],
      borderColor: [
        'rgb(99, 102, 241)',
        'rgb(16, 185, 129)'
      ],
      borderWidth: 2
    }]
  };

  const serviceTypeProfitOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { padding: 15, usePointStyle: true }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const data = serviceTypeProfits[context.dataIndex];
            return [
              `Profit: ${formatCurrency(context.parsed)}`,
              `Revenue: ${formatCurrency(data.revenue)}`,
              `Margin: ${data.margin.toFixed(1)}%`
            ];
          }
        }
      }
    }
  };

  const paymentStatusData = {
    labels: paymentStatus.map(p => p.status),
    datasets: [{
      data: paymentStatus.map(p => p.amount),
      backgroundColor: [
        'rgba(16, 185, 129, 0.8)',   // Paid - Green
        'rgba(251, 191, 36, 0.8)',   // Partially Paid - Yellow
        'rgba(239, 68, 68, 0.8)',    // Unpaid - Red
        'rgba(249, 115, 22, 0.8)'    // Overdue - Orange
      ],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  const paymentStatusOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: { padding: 10, usePointStyle: true }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `${context.label}: ${formatCurrency(context.parsed)} (${percentage}%)`;
          }
        }
      }
    }
  };

  const topServicesChartData = {
    labels: topProfitableServices.map(s =>
      s.name.length > 30 ? s.name.substring(0, 30) + '...' : s.name
    ),
    datasets: [{
      label: 'Profit',
      data: topProfitableServices.map(s => s.profit),
      backgroundColor: 'rgba(99, 102, 241, 0.8)',
      borderColor: 'rgb(99, 102, 241)',
      borderWidth: 1
    }]
  };

  const topServicesChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          afterLabel: function(context) {
            const service = topProfitableServices[context.dataIndex];
            return [
              `Revenue: ${formatCurrency(service.revenue)}`,
              `Cost: ${formatCurrency(service.cost)}`,
              `Margin: ${service.margin.toFixed(1)}%`
            ];
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { display: false },
        ticks: {
          callback: function(value) {
            return formatCurrency(value);
          }
        }
      },
      y: { grid: { display: false } }
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50">
        <div className="text-center">
          <MoonLoader size={60} color="#10B981" />
          <p className="mt-4 text-sm font-medium text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (role !== 'Service Advisor' && role !== 'Admin') {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-teal-50 py-4">
      <BreadCrumb pages={pages} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
                <CurrencyDollarIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Financial Analysis</h1>
                <p className="text-sm text-gray-600 mt-1">Revenue, profit margins, and payment status tracking</p>
              </div>
            </div>

            {/* Year Selector */}
            <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-xl shadow-md">
              <label className="text-sm font-medium text-gray-700">Year:</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm font-medium"
              >
                {[...Array(5)].map((_, i) => {
                  const year = new Date().getFullYear() - i;
                  return <option key={year} value={year}>{year}</option>;
                })}
              </select>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <MoonLoader size={50} color="#10B981" />
              <p className="mt-4 text-sm font-medium text-gray-600">Loading financial data...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Total Revenue */}
              <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg p-6 text-white transform transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-emerald-100 text-sm font-medium">Total Revenue</p>
                    <h3 className="text-2xl font-bold mt-1">{formatCurrency(totalRevenue)}</h3>
                  </div>
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                    <CurrencyDollarIcon className="w-7 h-7" />
                  </div>
                </div>
                <p className="text-emerald-100 text-xs">in {selectedYear}</p>
              </div>

              {/* Total Profit */}
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg p-6 text-white transform transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-blue-100 text-sm font-medium">Total Profit</p>
                    <h3 className="text-2xl font-bold mt-1">{formatCurrency(totalProfit)}</h3>
                  </div>
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                    <ArrowTrendingUpIcon className="w-7 h-7" />
                  </div>
                </div>
                <p className="text-blue-100 text-xs">Net profit for {selectedYear}</p>
              </div>

              {/* Profit Margin */}
              <div className="bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl shadow-lg p-6 text-white transform transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-orange-100 text-sm font-medium">Profit Margin</p>
                    <h3 className="text-2xl font-bold mt-1">{profitMargin.toFixed(1)}%</h3>
                  </div>
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                    <ChartBarIcon className="w-7 h-7" />
                  </div>
                </div>
                <p className="text-orange-100 text-xs">Average margin</p>
              </div>

              {/* Invoice Status */}
              <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl shadow-lg p-6 text-white transform transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-purple-100 text-sm font-medium">Invoice Status</p>
                    <h3 className="text-2xl font-bold mt-1">{paidInvoices}/{paidInvoices + outstandingInvoices}</h3>
                  </div>
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                    <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
                <p className="text-purple-100 text-xs">Paid invoices</p>
              </div>
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Monthly Revenue & Profit */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Monthly Revenue & Profit</h3>
                  <p className="text-sm text-gray-600 mt-1">Revenue and profit comparison throughout {selectedYear}</p>
                </div>
                <div style={{ height: '350px' }}>
                  <Bar data={monthlyRevenueChartData} options={monthlyRevenueChartOptions} />
                </div>
              </div>

              {/* Profit Margin Trend */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Profit Margin Trend</h3>
                  <p className="text-sm text-gray-600 mt-1">Monthly profit margin percentage</p>
                </div>
                <div style={{ height: '350px' }}>
                  <Line data={profitMarginLineData} options={profitMarginLineOptions} />
                </div>
              </div>

              {/* Services vs Parts Profit */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Profit by Type</h3>
                  <p className="text-sm text-gray-600 mt-1">Services vs Parts profitability</p>
                </div>
                <div style={{ height: '350px' }}>
                  {serviceTypeProfits.length > 0 ? (
                    <Doughnut data={serviceTypeProfitData} options={serviceTypeProfitOptions} />
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <p>No profit data available</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Status */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Payment Status Breakdown</h3>
                  <p className="text-sm text-gray-600 mt-1">Invoice payment distribution</p>
                </div>
                <div style={{ height: '350px' }}>
                  {paymentStatus.length > 0 ? (
                    <Doughnut data={paymentStatusData} options={paymentStatusOptions} />
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <p>No payment data available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Top Profitable Services */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-900">Top 10 Most Profitable Services</h3>
                <p className="text-sm text-gray-600 mt-1">Services generating the highest profit margins</p>
              </div>
              <div style={{ height: '400px' }}>
                {topProfitableServices.length > 0 ? (
                  <Bar data={topServicesChartData} options={topServicesChartOptions} />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <p>No service profit data available for {selectedYear}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Detailed Financial Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Service Type Details */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4">
                  <h3 className="text-xl font-bold text-white">Service Type Breakdown</h3>
                  <p className="text-emerald-100 text-sm mt-1">Financial performance by category</p>
                </div>
                <div className="p-6">
                  {serviceTypeProfits.map((service, index) => (
                    <div key={index} className="mb-6 last:mb-0">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-lg font-bold text-gray-900">{service.type}</h4>
                        <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                          service.margin >= 30 ? 'bg-green-100 text-green-700' :
                          service.margin >= 15 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {service.margin.toFixed(1)}% margin
                        </span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Revenue:</span>
                          <span className="font-semibold text-gray-900">{formatCurrency(service.revenue)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Cost:</span>
                          <span className="font-semibold text-gray-900">{formatCurrency(service.cost)}</span>
                        </div>
                        <div className="flex justify-between border-t pt-2">
                          <span className="text-gray-600 font-medium">Profit:</span>
                          <span className="font-bold text-emerald-600">{formatCurrency(service.profit)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Items Sold:</span>
                          <span className="font-semibold text-gray-900">{service.count}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Monthly Performance Table */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
                  <h3 className="text-xl font-bold text-white">Monthly Performance</h3>
                  <p className="text-blue-100 text-sm mt-1">Month-by-month financial summary</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Profit</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Margin</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {monthlyFinancials.map((data, index) => {
                        const margin = data.revenue > 0 ? ((data.profit / data.revenue) * 100) : 0;
                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][index]}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right font-semibold">
                              {formatCurrency(data.revenue)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right font-semibold">
                              {formatCurrency(data.profit)}
                            </td>
                            <td className="px-4 py-3 text-sm text-right">
                              <span className={`px-2 py-1 rounded text-xs font-bold ${
                                margin >= 30 ? 'bg-green-100 text-green-700' :
                                margin >= 15 ? 'bg-yellow-100 text-yellow-700' :
                                margin > 0 ? 'bg-orange-100 text-orange-700' :
                                'bg-gray-100 text-gray-500'
                              }`}>
                                {margin.toFixed(1)}%
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProfitAnalysis;
